import React, { useState, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Alert,
  Stack,
} from "@mui/material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArticleIcon from "@mui/icons-material/Article";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import LinkIcon from "@mui/icons-material/Link";
import LaunchIcon from "@mui/icons-material/Launch";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import DownloadIcon from "@mui/icons-material/Download";

const ResearchLibraryPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [bookmarkedItems, setBookmarkedItems] = useState(new Set());

  // Resource Data
  const resources = {
    clinicalGuidelines: [
      {
        id: "cg1",
        title: "2024 AHA Scientific Statement on Cardiac Amyloidosis",
        organization: "American Heart Association",
        date: "2024-01-15",
        type: "PDF",
        url: "https://www.ahajournals.org/cardiac-amyloidosis",
        description:
          "Comprehensive guidelines for diagnosis and management of cardiac amyloidosis, including ATTR and AL subtypes.",
        tags: ["Cardiac", "ATTR", "AL", "Diagnosis", "Treatment"],
      },
      {
        id: "cg2",
        title: "NCCN Guidelines: Systemic Light Chain Amyloidosis",
        organization: "National Comprehensive Cancer Network",
        date: "2024-03-01",
        type: "PDF",
        url: "https://www.nccn.org/guidelines",
        description:
          "Evidence-based clinical practice guidelines for diagnosis, risk stratification, and treatment of AL amyloidosis.",
        tags: ["AL", "Treatment", "Guidelines", "Chemotherapy"],
      },
      {
        id: "cg3",
        title:
          "Consensus Recommendations for the Diagnosis and Treatment of ATTR Amyloidosis",
        organization: "Amyloidosis Foundation",
        date: "2023-11-20",
        type: "PDF",
        url: "https://www.amyloidosis.org/guidelines",
        description:
          "International consensus on diagnostic approaches and therapeutic options for ATTR amyloidosis.",
        tags: ["ATTR", "ATTRv", "ATTRwt", "Diagnosis", "Treatment"],
      },
      {
        id: "cg4",
        title: "Renal Amyloidosis: Diagnosis and Management",
        organization: "American Society of Nephrology",
        date: "2023-09-10",
        type: "PDF",
        url: "https://www.asn-online.org/amyloidosis",
        description:
          "Guidelines for nephrologists on diagnosing and managing kidney involvement in amyloidosis.",
        tags: ["Renal", "Kidney", "AL", "AA", "Diagnosis"],
      },
      {
        id: "cg5",
        title: "Peripheral Neuropathy in Amyloidosis: Clinical Approach",
        organization: "American Academy of Neurology",
        date: "2023-07-15",
        type: "PDF",
        url: "https://www.aan.com/amyloid-neuropathy",
        description:
          "Guidelines for recognizing and treating peripheral and autonomic neuropathy in ATTRv amyloidosis.",
        tags: ["Neurological", "ATTRv", "Neuropathy", "Treatment"],
      },
    ],

    researchPapers: [
      {
        id: "rp1",
        title:
          "ATTR-ACT Study: Tafamidis Treatment for ATTR Cardiac Amyloidosis",
        journal: "New England Journal of Medicine",
        date: "2018-09-13",
        type: "Article",
        url: "https://www.nejm.org/doi/full/10.1056/NEJMoa1805689",
        description:
          "Landmark Phase 3 trial demonstrating efficacy of tafamidis in reducing mortality and cardiovascular hospitalizations.",
        tags: ["ATTR", "Cardiac", "Clinical Trial", "Tafamidis"],
      },
      {
        id: "rp2",
        title:
          "Patisiran for Hereditary Transthyretin Amyloidosis (APOLLO Study)",
        journal: "New England Journal of Medicine",
        date: "2018-07-05",
        type: "Article",
        url: "https://www.nejm.org/doi/full/10.1056/NEJMoa1716153",
        description:
          "Phase 3 study of RNA interference therapy for ATTRv polyneuropathy showing significant improvement.",
        tags: ["ATTRv", "Neurological", "Gene Therapy", "Clinical Trial"],
      },
      {
        id: "rp3",
        title: "Daratumumab Plus CyBorD in AL Amyloidosis",
        journal: "Blood",
        date: "2021-03-25",
        type: "Article",
        url: "https://ashpublications.org/blood/article/137/9/1199",
        description:
          "Study evaluating daratumumab-based therapy achieving high hematologic response rates in AL amyloidosis.",
        tags: ["AL", "Chemotherapy", "Clinical Trial", "Treatment"],
      },
      {
        id: "rp4",
        title: "Cardiac Biomarkers in Amyloidosis: Prognostic Value",
        journal: "Journal of the American College of Cardiology",
        date: "2023-05-10",
        type: "Article",
        url: "https://www.jacc.org/doi/10.1016/amyloidosis",
        description:
          "Analysis of NT-proBNP and troponin levels for risk stratification and prognosis in cardiac amyloidosis.",
        tags: ["Cardiac", "Biomarkers", "Prognosis", "NT-proBNP"],
      },
      {
        id: "rp5",
        title: "Next-Generation Sequencing in TTR Gene Mutations",
        journal: "Genetics in Medicine",
        date: "2023-08-20",
        type: "Article",
        url: "https://www.gimjournal.org/ttr-mutations",
        description:
          "Comprehensive analysis of TTR gene variants and their clinical implications in hereditary amyloidosis.",
        tags: ["ATTRv", "Genetics", "Diagnosis", "Research"],
      },
      {
        id: "rp6",
        title: "Bone Scintigraphy for Non-Invasive Diagnosis of ATTR",
        journal: "European Heart Journal",
        date: "2023-02-14",
        type: "Article",
        url: "https://academic.oup.com/eurheartj/cardiac-amyloid",
        description:
          "Study validating 99mTc-PYP/DPD scintigraphy as a non-biopsy diagnostic method for ATTR cardiac amyloidosis.",
        tags: ["ATTR", "Cardiac", "Diagnosis", "Imaging"],
      },
    ],

    educationalVideos: [
      {
        id: "ev1",
        title: "Understanding Amyloidosis: Patient Education Series",
        source: "Mayo Clinic",
        duration: "15:30",
        type: "Video",
        url: "https://www.mayoclinic.org/amyloidosis-videos",
        description:
          "Comprehensive overview of amyloidosis types, symptoms, and treatment options for patients and families.",
        tags: ["Education", "Overview", "Patient Resources"],
      },
      {
        id: "ev2",
        title: "Cardiac Amyloidosis: What You Need to Know",
        source: "Cleveland Clinic",
        duration: "12:45",
        type: "Video",
        url: "https://my.clevelandclinic.org/cardiac-amyloid",
        description:
          "Expert cardiologists explain cardiac amyloidosis symptoms, diagnosis with echocardiography and MRI.",
        tags: ["Cardiac", "Education", "Diagnosis"],
      },
      {
        id: "ev3",
        title: "Living with ATTR Amyloidosis: Patient Perspectives",
        source: "Amyloidosis Foundation",
        duration: "20:15",
        type: "Video",
        url: "https://www.amyloidosis.org/patient-stories",
        description:
          "Real patient stories sharing experiences with diagnosis, treatment, and daily life management.",
        tags: ["ATTR", "Patient Stories", "Support"],
      },
      {
        id: "ev4",
        title: "AL Amyloidosis Treatment Options Explained",
        source: "Johns Hopkins Medicine",
        duration: "18:00",
        type: "Video",
        url: "https://www.hopkinsmedicine.org/al-amyloidosis",
        description:
          "Hematologists discuss chemotherapy, stem cell transplant, and novel therapies for AL amyloidosis.",
        tags: ["AL", "Treatment", "Chemotherapy"],
      },
      {
        id: "ev5",
        title: "Gene Therapy for Hereditary Amyloidosis",
        source: "National Institutes of Health",
        duration: "25:00",
        type: "Video",
        url: "https://www.nih.gov/gene-therapy-amyloidosis",
        description:
          "In-depth explanation of RNA interference and gene silencing therapies for ATTRv amyloidosis.",
        tags: ["ATTRv", "Gene Therapy", "Research"],
      },
    ],

    externalResources: [
      {
        id: "er1",
        title: "Amyloidosis Foundation",
        type: "Link",
        url: "https://www.amyloidosis.org",
        description:
          "Leading patient advocacy organization providing education, support groups, and research funding for all types of amyloidosis.",
        tags: ["Support", "Patient Resources", "Advocacy"],
      },
      {
        id: "er2",
        title: "ClinicalTrials.gov - Amyloidosis Studies",
        type: "Link",
        url: "https://clinicaltrials.gov/search?term=amyloidosis",
        description:
          "Official NIH database of all registered clinical trials for amyloidosis worldwide.",
        tags: ["Clinical Trials", "Research"],
      },
      {
        id: "er3",
        title: "National Organization for Rare Disorders (NORD)",
        type: "Link",
        url: "https://rarediseases.org/rare-diseases/amyloidosis",
        description:
          "Comprehensive information on amyloidosis as a rare disease, including financial assistance programs.",
        tags: ["Support", "Financial Aid", "Patient Resources"],
      },
      {
        id: "er4",
        title: "Boston University Amyloidosis Center",
        type: "Link",
        url: "https://www.bu.edu/amyloid",
        description:
          "World-renowned center providing expert care, research, and education on all forms of amyloidosis.",
        tags: ["Research", "Treatment Center", "Expertise"],
      },
      {
        id: "er5",
        title: "Amyloidosis Support Groups",
        type: "Link",
        url: "https://www.amyloidosissupport.org",
        description:
          "Online and in-person support groups connecting patients and caregivers for peer support and shared experiences.",
        tags: ["Support", "Community", "Mental Health"],
      },
      {
        id: "er6",
        title: "European Network for Cardiac Amyloidosis",
        type: "Link",
        url: "https://www.enc-amyloidosis.org",
        description:
          "International collaborative network for cardiac amyloidosis research and clinical excellence.",
        tags: ["Cardiac", "Research", "International"],
      },
    ],
  };

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = new Set();
    Object.values(resources).forEach((category) => {
      category.forEach((item) => {
        item.tags.forEach((tag) => tags.add(tag));
      });
    });
    return Array.from(tags).sort();
  }, []);

  // Filter resources based on search and tags
  const getFilteredResources = (category) => {
    return resources[category].filter((item) => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        item.title.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        item.tags.some((tag) => tag.toLowerCase().includes(searchLower));

      // Tag filter
      const matchesTags =
        selectedTags.length === 0 ||
        selectedTags.every((tag) => item.tags.includes(tag));

      return matchesSearch && matchesTags;
    });
  };

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleBookmark = (id) => {
    setBookmarkedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case "PDF":
        return <PictureAsPdfIcon />;
      case "Article":
        return <ArticleIcon />;
      case "Video":
        return <VideoLibraryIcon />;
      case "Link":
        return <LinkIcon />;
      default:
        return <ArticleIcon />;
    }
  };

  const getResourceColor = (type) => {
    switch (type) {
      case "PDF":
        return "#d32f2f";
      case "Article":
        return "#1976d2";
      case "Video":
        return "#9c27b0";
      case "Link":
        return "#2e7d32";
      default:
        return "#1A5EA8";
    }
  };

  const renderResourceCard = (item) => (
    <Grid item xs={12} sm={6} md={4} key={item.id}>
      <Card
        elevation={3}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.3s",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: 6,
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "start",
              mb: 2,
            }}
          >
            <Chip
              icon={getResourceIcon(item.type)}
              label={item.type}
              size="small"
              sx={{
                bgcolor: `${getResourceColor(item.type)}15`,
                color: getResourceColor(item.type),
                fontWeight: 600,
              }}
            />
            <IconButton size="small" onClick={() => handleBookmark(item.id)}>
              {bookmarkedItems.has(item.id) ? (
                <BookmarkIcon color="primary" />
              ) : (
                <BookmarkBorderIcon />
              )}
            </IconButton>
          </Box>

          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            sx={{ minHeight: 60 }}
          >
            {item.title}
          </Typography>

          {item.organization && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              gutterBottom
            >
              {item.organization}
            </Typography>
          )}

          {item.journal && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              gutterBottom
            >
              {item.journal}
            </Typography>
          )}

          {item.source && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              gutterBottom
            >
              Source: {item.source}
            </Typography>
          )}

          {item.duration && (
            <Chip label={item.duration} size="small" sx={{ mb: 1 }} />
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{ mt: 1 }}
          >
            {item.description}
          </Typography>

          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 2 }}>
            {item.tags.slice(0, 3).map((tag, idx) => (
              <Chip
                key={idx}
                label={tag}
                size="small"
                variant="outlined"
                onClick={() => handleTagToggle(tag)}
                sx={{
                  cursor: "pointer",
                  bgcolor: selectedTags.includes(tag)
                    ? "rgba(102, 126, 234, 0.1)"
                    : "transparent",
                  borderColor: selectedTags.includes(tag)
                    ? "#1A5EA8"
                    : undefined,
                }}
              />
            ))}
            {item.tags.length > 3 && (
              <Chip
                label={`+${item.tags.length - 3}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </CardContent>

        <CardActions>
          <Button
            fullWidth
            variant="contained"
            endIcon={<LaunchIcon />}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            }}
          >
            View Resource
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  const tabContent = [
    { label: "Clinical Guidelines", key: "clinicalGuidelines" },
    { label: "Research Papers", key: "researchPapers" },
    { label: "Educational Videos", key: "educationalVideos" },
    { label: "External Resources", key: "externalResources" },
  ];

  const currentResources = getFilteredResources(tabContent[activeTab].key);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <LibraryBooksIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Research Library
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Curated educational resources on amyloidosis
        </Typography>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Access clinical guidelines, research papers, educational videos, and
        support resources from leading medical institutions and organizations.
      </Alert>

      {/* Search Bar */}
      <Paper elevation={3} sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search resources by title, description, or tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm("")}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Tag Filters */}
      {allTags.length > 0 && (
        <Paper elevation={2} sx={{ mb: 3, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Filter by Tags:
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {allTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onClick={() => handleTagToggle(tag)}
                color={selectedTags.includes(tag) ? "primary" : "default"}
                variant={selectedTags.includes(tag) ? "filled" : "outlined"}
                size="small"
                sx={{ cursor: "pointer" }}
              />
            ))}
            {selectedTags.length > 0 && (
              <Chip
                label="Clear All"
                onClick={() => setSelectedTags([])}
                size="small"
                color="error"
                variant="outlined"
                onDelete={() => setSelectedTags([])}
              />
            )}
          </Box>
        </Paper>
      )}

      {/* Tabs */}
      <Paper elevation={3}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          {tabContent.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {tab.label}
                  <Chip
                    label={getFilteredResources(tab.key).length}
                    size="small"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                </Box>
              }
              sx={{ textTransform: "none", fontWeight: 500 }}
            />
          ))}
        </Tabs>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {currentResources.length > 0 ? (
            <Grid container spacing={3}>
              {currentResources.map(renderResourceCard)}
            </Grid>
          ) : (
            <Paper
              elevation={0}
              sx={{
                textAlign: "center",
                py: 8,
                px: 4,
                bgcolor: "background.default",
              }}
            >
              <SearchIcon
                sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
              />
              <Typography variant="h5" color="text.secondary" gutterBottom>
                No resources found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your search or filters
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedTags([]);
                }}
                startIcon={<ClearIcon />}
              >
                Clear Filters
              </Button>
            </Paper>
          )}
        </Box>
      </Paper>

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Disclaimer:</strong> The resources provided in this library
          are for informational and educational purposes only. They do not
          constitute medical advice. Always consult with qualified healthcare
          professionals regarding your specific medical condition and treatment
          options.
        </Typography>
      </Paper>
    </Container>
  );
};

export default ResearchLibraryPage;
